// components/SEO.jsx
import React from "react";
import { Helmet } from "react-helmet"; // или react-helmet-async

const SITE = {
    name: "Virtalibry",
    alternateName: "Virtalibry.Com",
    baseUrl: "https://virtalibry.com",
    defaultTitle: "Virtalibry – Verified Amazon Book Reviews & Marketing Tools for Self-Publishers",
    defaultDescription:
        "Virtalibry is a trusted platform for authors to get verified Amazon reviews, run A/B polls for book covers & titles, and grow visibility. Start free!",
    defaultImage: "https://virtalibry.com/face/why_love_is_gold_new.png",
    defaultKeywords:
        "Amazon book reviews, get book reviews, KDP promotion, test book cover, test book title, book review platform, verified Amazon reviews, Virtalibry, author marketing tools",
    defaultLocale: "en_US",
    twitterSite: "@virtalibry",
    sameAs: [
        "https://www.facebook.com/virtalibry",
        "https://twitter.com/virtalibry",
        "https://www.linkedin.com/company/virtalibry",
    ],
};

// ---- i18n config ----
const I18N = {
    defaultLang: "en",
    langs: ["en",
    //    "ru", "uk", "es", "pt", "de", "fr", "it"
    ],
    prefixes: { en: "",
     //   ru: "/ru", uk: "/uk", es: "/es", pt: "/pt", de: "/de", fr: "/fr", it: "/it"
    },
    ogLocales: { en: "en_US",
      //  ru: "ru_RU", uk: "uk_UA", es: "es_ES", pt: "pt_PT", de: "de_DE", fr: "fr_FR", it: "it_IT"
    },
};

const toLD = (obj) => ({ __html: JSON.stringify(obj) });

/** JSON-LD builders */
const buildWebSite = ({ searchUrl, sameAs }) => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    alternateName: SITE.alternateName,
    url: SITE.baseUrl,
    ...(Array.isArray(sameAs) && sameAs.length ? { sameAs } : {}),
    potentialAction: searchUrl
        ? {
            "@type": "SearchAction",
            target: searchUrl,
            "query-input": "required name=search_term_string",
        }
        : undefined,
});

const buildOrganization = (org) =>
    org && {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: org.name || SITE.name,
        alternateName: org.alternateName || SITE.alternateName,
        url: org.url || SITE.baseUrl,
        logo: org.logo || SITE.defaultImage,
        sameAs: org.sameAs || [],
        contactPoint: org.contactPoint || undefined,
    };

const buildWebPage = (page) =>
    page && {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: page.name,
        url: page.url,
        description: page.description,
        breadcrumb: page.breadcrumbUrl || undefined,
        datePublished: page.datePublished,
        dateModified: page.dateModified,
    };

const buildBreadcrumb = (items) =>
    Array.isArray(items) &&
    items.length > 0 && {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: it.item,
        })),
    };

const buildArticle = (a) =>
    a && {
        "@context": "https://schema.org",
        "@type": a.type || "Article",
        headline: a.headline,
        description: a.description,
        image: a.image,
        author: a.author && { "@type": "Person", name: a.author },
        publisher:
            a.publisher && {
                "@type": "Organization",
                name: a.publisher,
                logo:
                    a.publisherLogo && {
                        "@type": "ImageObject",
                        url: a.publisherLogo,
                    },
            },
        datePublished: a.datePublished,
        dateModified: a.dateModified,
        mainEntityOfPage: a.url && { "@type": "WebPage", "@id": a.url },
    };

const buildProduct = (p) =>
    p && {
        "@context": "https://schema.org",
        "@type": p.type || "Product",
        name: p.name,
        description: p.description,
        image: p.image,
        brand: p.brand && { "@type": "Brand", name: p.brand },
        sku: p.sku,
        aggregateRating:
            p.aggregateRating && {
                "@type": "AggregateRating",
                ratingValue: p.aggregateRating.ratingValue,
                reviewCount: p.aggregateRating.reviewCount,
            },
        offers:
            p.offers && {
                "@type": "Offer",
                url: p.offers.url,
                priceCurrency: p.offers.priceCurrency,
                price: p.offers.price,
                availability: p.offers.availability,
            },
    };

const buildWebApp = (app) =>
    app && {
        "@context": "https://schema.org",
        "@type": app.type || "WebApplication",
        name: app.name || SITE.name,
        url: app.url || SITE.baseUrl,
        applicationCategory: app.applicationCategory || "BusinessApplication",
        operatingSystem: app.operatingSystem || "All",
        offers:
            app.offers && {
                "@type": "Offer",
                price: app.offers.price,
                priceCurrency: app.offers.priceCurrency,
                url: app.offers.url,
            },
        aggregateRating:
            app.aggregateRating && {
                "@type": "AggregateRating",
                ratingValue: app.aggregateRating.ratingValue,
                reviewCount: app.aggregateRating.reviewCount,
            },
    };

const buildVideo = (v) =>
    v && {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: v.name,
        description: v.description,
        thumbnailUrl: v.thumbnailUrl,
        uploadDate: v.uploadDate,
        duration: v.duration,
        contentUrl: v.contentUrl,
        embedUrl: v.embedUrl,
    };

const buildHowTo = (h) =>
    h && {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: h.name,
        description: h.description,
        totalTime: h.totalTime,
        supply: h.supply,
        tool: h.tool,
        step: Array.isArray(h.steps)
            ? h.steps.map((s) => (typeof s === "string" ? { "@type": "HowToStep", text: s } : s))
            : undefined,
    };

// ---- helpers: i18n detection + alternates ----
function safeParseUrl(u, fallbackBase) {
    try {
        return new URL(u);
    } catch {
        try {
            return new URL(String(u || "/"), fallbackBase);
        } catch {
            return null;
        }
    }
}

function detectLang({ pageUrl, locale }) {
    const byLocale = Object.entries(I18N.ogLocales).find(([, og]) => og === locale);
    if (byLocale?.[0]) return byLocale[0];

    const parsed = safeParseUrl(pageUrl, SITE.baseUrl);
    const path = parsed?.pathname || "/";
    for (const lang of I18N.langs) {
        const pfx = I18N.prefixes[lang];
        if (pfx && (path === pfx || path.startsWith(pfx + "/"))) return lang;
    }
    return I18N.defaultLang;
}

function stripKnownPrefix(pathname) {
    for (const lang of I18N.langs) {
        const pfx = I18N.prefixes[lang];
        if (!pfx) continue;
        if (pathname === pfx) return "/";
        if (pathname.startsWith(pfx + "/")) return pathname.slice(pfx.length) || "/";
    }
    return pathname || "/";
}

function buildAlternates({ pageUrl }) {
    const parsed = safeParseUrl(pageUrl, SITE.baseUrl);
    if (!parsed) return [];

    const basePath = stripKnownPrefix(parsed.pathname);
    const origin = `${parsed.protocol}//${parsed.host}`;
    const qs = parsed.search || "";

    const links = I18N.langs.map((lang) => {
        const pfx = I18N.prefixes[lang] || "";
        return { hrefLang: lang, href: `${origin}${pfx}${basePath}${qs}` };
    });

    const en = links.find((l) => l.hrefLang === I18N.defaultLang);
    if (en) links.push({ hrefLang: "x-default", href: en.href });

    return links;
}

function hasAnyLangPrefix(pathname) {
    const p = pathname || "/";
    for (const lang of I18N.langs) {
        const pfx = I18N.prefixes[lang];
        if (!pfx) continue;
        if (p === pfx || p.startsWith(pfx + "/")) return true;
    }
    return false;
}

function looksEnglishPath(pathname) {
    return !hasAnyLangPrefix(pathname || "/");
}

function shouldOverrideCanonical({ canonical, pageUrl, detectedLang, forceCanonical }) {
    if (forceCanonical) return false;
    if (!canonical) return false;
    if (detectedLang === "en") return false;

    const can = safeParseUrl(canonical, SITE.baseUrl);
    const pag = safeParseUrl(pageUrl, SITE.baseUrl);
    if (!can || !pag) return false;

    const canPath = can.pathname || "/";
    const pagePath = pag.pathname || "/";

    return hasAnyLangPrefix(pagePath) && looksEnglishPath(canPath);
}

export default function SEO({
                                title,
                                description,
                                keywords,
                                url,
                                image,
                                type = "website",

                                twitterImage,
                                twitterCard = "summary_large_image",

                                canonical,
                                noindex = false,
                                nofollow = false,
                                locale = SITE.defaultLocale,
                                siteName = SITE.name,

                                // ✅ legacy extras you already use
                                ogTitle,
                                twitterTitle,

                                // ✅ i18n additions
                                autoI18n = true,
                                forceCanonical = false,
                                alternatesOverride = null,

                                // JSON-LD
                                faq = [],
                                searchUrl = `${SITE.baseUrl}/library?q={search_term_string}`,
                                organization,
                                webPage,
                                breadcrumb,
                                article,
                                product,
                                webApp,
                                video,
                                howto,
                                additionalSchemas = [],
                            }) {
    const pageTitle = title || SITE.defaultTitle;
    const pageDesc = description || SITE.defaultDescription;
    const pageUrl = url || SITE.baseUrl;
    const pageImage = image || SITE.defaultImage;
    const pageKeywords = keywords || SITE.defaultKeywords;

    const robots = [noindex ? "noindex" : "index", nofollow ? "nofollow" : "follow"].join(", ");

    const detectedLang = autoI18n ? detectLang({ pageUrl, locale }) : I18N.defaultLang;
    const htmlLang = detectedLang;
    const ogLocale = autoI18n ? (I18N.ogLocales[detectedLang] || SITE.defaultLocale) : locale;

    const overrideCanonical = autoI18n
        ? shouldOverrideCanonical({ canonical, pageUrl, detectedLang, forceCanonical })
        : false;

    const canonicalHref = overrideCanonical ? pageUrl : (canonical || pageUrl);

    const alternates = alternatesOverride || (autoI18n ? buildAlternates({ pageUrl }) : []);

    const ogAlternateLocales =
        autoI18n
            ? I18N.langs
                .map((l) => I18N.ogLocales[l])
                .filter(Boolean)
                .filter((l) => l !== ogLocale)
            : [];

    const webSiteSchema = buildWebSite({ searchUrl, sameAs: SITE.sameAs });

    const faqSchema =
        Array.isArray(faq) && faq.length
            ? {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faq.map(({ q, a }) => ({
                    "@type": "Question",
                    name: q,
                    acceptedAnswer: { "@type": "Answer", text: a },
                })),
            }
            : null;

    const schemas = [
        buildOrganization(organization),
        buildWebPage(webPage),
        buildBreadcrumb(breadcrumb),
        buildArticle(article),
        buildProduct(product),
        buildWebApp(webApp),
        buildVideo(video),
        buildHowTo(howto),
        ...additionalSchemas.filter(Boolean),
    ].filter(Boolean);

    return (
        <Helmet htmlAttributes={{ lang: htmlLang }}>
            {/* Base */}
            <title>{pageTitle}</title>
            <meta name="description" content={pageDesc} />
            {pageKeywords && <meta name="keywords" content={pageKeywords} />}
            <meta name="robots" content={robots} />
            <meta httpEquiv="content-language" content={htmlLang} />

            {/* Canonical */}
            <link rel="canonical" href={canonicalHref} />

            {/* hreflang */}
            {alternates.map((a, i) => (
                <link key={`alt-${i}`} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
            ))}

            {/* Open Graph */}
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content={ogLocale} />
            {ogAlternateLocales.map((loc) => (
                <meta key={loc} property="og:locale:alternate" content={loc} />
            ))}
            <meta property="og:title" content={ogTitle || pageTitle} />
            <meta property="og:description" content={pageDesc} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:image" content={pageImage} />
            <meta property="og:image:alt" content={siteName} />

            {/* Twitter */}
            <meta name="twitter:card" content={twitterCard} />
            <meta name="twitter:title" content={twitterTitle || ogTitle || pageTitle} />
            <meta name="twitter:description" content={pageDesc} />
            <meta name="twitter:image" content={twitterImage || pageImage} />
            {SITE.twitterSite && <meta name="twitter:site" content={SITE.twitterSite} />}

            {/* JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={toLD(webSiteSchema)} />
            {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={toLD(faqSchema)} />}
            {schemas.map((s, i) => (
                <script key={i} type="application/ld+json" dangerouslySetInnerHTML={toLD(s)} />
            ))}
        </Helmet>
    );
}
